import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Drawer,
  IconButton,
  LinearProgress,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUploadOutlined";
import FolderOpenIcon from "@mui/icons-material/FolderOpenOutlined";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFileOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/WarningAmber";
import ErrorIcon from "@mui/icons-material/ErrorOutline";
import VisibilityIcon from "@mui/icons-material/VisibilityOutlined";
import * as bankApi from "./contentBankApi";
import { formatBytes } from "./format";

const HTML_RE = /\.html$/i;
const ASSET_RE = /\.(css|js|png|jpe?g|gif|svg|webp|avif|ico|woff2?|ttf|otf|eot|json|mp4|webm|mp3|wav)$/i;

const titleFromFilename = (name) =>
  name.replace(HTML_RE, "").replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();

/**
 * Upload one chapter (its .html plus assets) into a module: a new chapter, or
 * a new version of an existing one. Same steps as the old publisher:
 * files -> upload -> check references (upload anything missing) -> preview -> publish.
 */
const ChapterUploadDrawer = ({ open, module, chapter, audience, onClose, onPublished }) => {
  const isReplace = !!chapter;
  const fileInput = useRef(null);
  const folderInput = useRef(null);
  const missingInput = useRef(null);

  const [step, setStep] = useState("files"); // files | uploading | review | publishing | done
  const [title, setTitle] = useState("");
  const [titleTouched, setTitleTouched] = useState(false);
  const [files, setFiles] = useState([]);
  const [skipped, setSkipped] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, current: "" });
  const [draft, setDraft] = useState(null); // { draftId, htmlKey }
  const [review, setReview] = useState(null); // parse result
  const [missingTarget, setMissingTarget] = useState(null);
  const [uploadingMissing, setUploadingMissing] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setStep("files");
    setTitle(isReplace ? chapter.title : "");
    setTitleTouched(false);
    setFiles([]);
    setSkipped([]);
    setProgress({ done: 0, total: 0, current: "" });
    setDraft(null);
    setReview(null);
    setShowPreview(false);
    setError("");
  }, [open, isReplace, chapter]);

  const htmlFiles = files.filter((f) => HTML_RE.test(f.name));
  const htmlFile = htmlFiles[0];

  const addFiles = (incoming) => {
    const accepted = [];
    const rejected = [];
    Array.from(incoming).forEach((file) => {
      if (HTML_RE.test(file.name) || ASSET_RE.test(file.name)) accepted.push(file);
      else rejected.push(file.name);
    });
    setFiles((current) => {
      const byName = new Map(current.map((f) => [f.name, f]));
      accepted.forEach((f) => byName.set(f.name, f));
      const next = Array.from(byName.values());
      const html = next.find((f) => HTML_RE.test(f.name));
      if (html && !isReplace && !titleTouched) setTitle(titleFromFilename(html.name));
      return next;
    });
    setSkipped(rejected);
    setError("");
  };

  const missingAssets = (review?.draft?.assets || []).filter((a) => a.status === "missing");

  const runParse = async (draftInfo) => {
    const result = await bankApi.parseDraft(draftInfo.draftId, draftInfo.htmlKey);
    setReview(result);
    return result;
  };

  const startUpload = async () => {
    if (htmlFiles.length !== 1) {
      setError("Add exactly one .html file — one chapter per upload.");
      return;
    }
    if (!isReplace && !title.trim()) {
      setError("Give the chapter a title.");
      return;
    }
    setError("");
    setStep("uploading");
    try {
      const metadata = files.map((f) => ({
        filename: f.name,
        content_type: f.type || "application/octet-stream",
        size: f.size,
      }));
      const created = await bankApi.createDraft(module.id, metadata, {
        chapterId: chapter?.id,
        chapterTitle: isReplace ? "" : title.trim(),
      });
      const urls = created.upload_urls || [];
      setProgress({ done: 0, total: files.length, current: "" });
      for (let i = 0; i < files.length; i += 1) {
        const file = files[i];
        const target = urls.find((u) => u.filename === file.name);
        if (!target || target.error) continue;
        setProgress({ done: i, total: files.length, current: file.name });
        await bankApi.uploadFile(target.upload_url, file, file.type || "application/octet-stream");
      }
      setProgress({ done: files.length, total: files.length, current: "" });
      const info = {
        draftId: created.draft_id,
        htmlKey: `${created.staging_prefix}${created.draft?.html_filename || htmlFile.name}`,
      };
      setDraft(info);
      await runParse(info);
      setStep("review");
    } catch (err) {
      setError(bankApi.errorMessage(err, "Upload failed"));
      setStep("files");
    }
  };

  const uploadMissing = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !missingTarget || !draft) return;
    setUploadingMissing(missingTarget);
    setError("");
    try {
      const contentType = file.type || "application/octet-stream";
      const { upload_url: uploadUrl } = await bankApi.getMissingUploadUrl(draft.draftId, missingTarget, contentType);
      await bankApi.uploadFile(uploadUrl, file, contentType);
      // Re-check: the server decides what is still missing and whether it can publish.
      await runParse(draft);
    } catch (err) {
      setError(bankApi.errorMessage(err, "Couldn't upload that file"));
    } finally {
      setUploadingMissing(null);
      setMissingTarget(null);
    }
  };

  const publish = async () => {
    setStep("publishing");
    setError("");
    try {
      const result = await bankApi.publishDraft(draft.draftId);
      setStep("done");
      onPublished?.(result.publication);
    } catch (err) {
      setError(bankApi.errorMessage(err, "Publishing failed"));
      setStep("review");
    }
  };

  const close = async () => {
    if (draft && step !== "done") {
      bankApi.discardDraft(draft.draftId).catch(() => {});
    }
    onClose();
  };

  const busy = step === "uploading" || step === "publishing" || !!uploadingMissing;
  const checks = review?.preflight_checks || [];
  const canPublish = !!review?.can_publish;

  return (
    <Drawer anchor="right" open={open} onClose={busy ? undefined : close}
      PaperProps={{ sx: { width: { xs: "100%", sm: 620 }, display: "flex", flexDirection: "column" } }}>
      <Box sx={{ p: "20px 24px", borderBottom: "1px solid #E5E7EB", display: "flex", gap: 2, alignItems: "flex-start" }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 700 }}>
            {isReplace ? "Upload a new version" : "Add a chapter"}
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#6B7280", mt: 0.5 }} noWrap>
            {module?.name}{isReplace ? ` · ${chapter.title} (now v${chapter.version})` : ""}
          </Typography>
        </Box>
        <IconButton onClick={close} disabled={busy} aria-label="Close"><CloseIcon /></IconButton>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", p: 3 }}>
        <StepBar step={step} />

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {step === "files" && (
          <>
            {!isReplace && (
              <TextField fullWidth size="small" label="Chapter title" value={title} sx={{ mb: 2.5 }}
                onChange={(e) => { setTitle(e.target.value); setTitleTouched(true); }}
                helperText="Filled from the file name; change it if you like." />
            )}
            <Box
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
              sx={{
                border: "2px dashed", borderColor: dragOver ? "#0172A6" : "#CBD5E1", borderRadius: 2,
                bgcolor: dragOver ? "#EFF6FB" : "#F8FAFC", p: 3, textAlign: "center",
              }}
            >
              <CloudUploadIcon sx={{ fontSize: 40, color: "#0172A6" }} />
              <Typography sx={{ fontWeight: 600, mt: 1 }}>Drop the chapter's .html and its assets here</Typography>
              <Typography sx={{ fontSize: 13, color: "#6B7280", mt: 0.5 }}>
                Keep your local structure, e.g. <code>chapter.html</code> with its <code>images/</code> and{" "}
                <code>katex-assets/</code> folders. HTML only — one chapter per upload.
              </Typography>
              <Box sx={{ display: "flex", gap: 1, justifyContent: "center", mt: 2, flexWrap: "wrap" }}>
                <Button variant="outlined" startIcon={<InsertDriveFileIcon />} onClick={() => fileInput.current?.click()}>
                  Choose files
                </Button>
                <Button variant="outlined" startIcon={<FolderOpenIcon />} onClick={() => folderInput.current?.click()}>
                  Choose folder
                </Button>
              </Box>
              <input ref={fileInput} type="file" multiple hidden
                accept=".html,.css,.js,.png,.jpg,.jpeg,.gif,.svg,.webp,.woff,.woff2,.ttf,.otf,.eot,.json"
                onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
              <input ref={folderInput} type="file" multiple hidden webkitdirectory="" directory=""
                onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
            </Box>

            {skipped.length > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Skipped {skipped.length} file{skipped.length > 1 ? "s" : ""} that aren't HTML or web assets:{" "}
                {skipped.slice(0, 5).join(", ")}{skipped.length > 5 ? "…" : ""}
              </Alert>
            )}
            {htmlFiles.length > 1 && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {htmlFiles.length} HTML files added. Upload one chapter at a time — remove the others.
              </Alert>
            )}

            {files.length > 0 && (
              <Box sx={{ mt: 2, border: "1px solid #E5E7EB", borderRadius: 2, maxHeight: 280, overflowY: "auto" }}>
                {[...files].sort((a, b) => HTML_RE.test(b.name) - HTML_RE.test(a.name) || a.name.localeCompare(b.name))
                  .map((file) => (
                    <Box key={file.name} sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 0.75,
                      borderBottom: "1px solid #F1F5F9", fontSize: 13 }}>
                      {HTML_RE.test(file.name) && <Chip size="small" color="primary" label="Chapter" />}
                      <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {file.webkitRelativePath || file.name}
                      </Box>
                      <Box sx={{ color: "#6B7280" }}>{formatBytes(file.size)}</Box>
                      <IconButton size="small" aria-label={`Remove ${file.name}`}
                        onClick={() => setFiles((cur) => cur.filter((f) => f.name !== file.name))}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
              </Box>
            )}
          </>
        )}

        {step === "uploading" && (
          <Box sx={{ py: 4 }}>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>
              {progress.total ? `Uploading ${Math.min(progress.done + 1, progress.total)} of ${progress.total}` : "Preparing…"}
            </Typography>
            <LinearProgress variant={progress.total ? "determinate" : "indeterminate"}
              value={progress.total ? (progress.done / progress.total) * 100 : 0} />
            <Typography sx={{ fontSize: 13, color: "#6B7280", mt: 1 }} noWrap>{progress.current || "Checking references…"}</Typography>
          </Box>
        )}

        {(step === "review" || step === "publishing") && review && (
          <>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1.5, mb: 2.5 }}>
              <Stat label="References" value={review.references_found} />
              <Stat label="Uploaded" value={review.uploaded_count} tone="good" />
              <Stat label="Missing" value={missingAssets.length} tone={missingAssets.length ? "bad" : "good"} />
            </Box>

            {missingAssets.length > 0 && (
              <Box sx={{ border: "1px solid #FECACA", bgcolor: "#FEF2F2", borderRadius: 2, p: 2, mb: 2.5 }}>
                <Typography sx={{ fontWeight: 600, color: "#B91C1C", mb: 1 }}>
                  The chapter uses {missingAssets.length} file{missingAssets.length > 1 ? "s" : ""} you didn't upload
                </Typography>
                {missingAssets.map((asset) => (
                  <Box key={asset.local_path} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
                    <Box sx={{ flex: 1, minWidth: 0, fontSize: 13, fontFamily: "monospace", overflow: "hidden",
                      textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{asset.local_path}</Box>
                    <Button size="small" variant="outlined" disabled={!!uploadingMissing}
                      startIcon={uploadingMissing === asset.local_path ? <CircularProgress size={14} /> : null}
                      onClick={() => { setMissingTarget(asset.local_path); missingInput.current?.click(); }}>
                      Upload
                    </Button>
                  </Box>
                ))}
                <input ref={missingInput} type="file" hidden onChange={uploadMissing} />
              </Box>
            )}

            {checks.length > 0 && (
              <Box sx={{ border: "1px solid #E5E7EB", borderRadius: 2, p: 2, mb: 2.5 }}>
                <Typography sx={{ fontWeight: 600, mb: 1 }}>Checks</Typography>
                {checks.map((check) => (
                  <Box key={check.id || check.label} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5, fontSize: 14 }}>
                    {check.state === "pass" ? <CheckCircleIcon sx={{ color: "#16A34A", fontSize: 18 }} />
                      : check.state === "warn" ? <WarningIcon sx={{ color: "#D97706", fontSize: 18 }} />
                        : <ErrorIcon sx={{ color: "#DC2626", fontSize: 18 }} />}
                    <Box sx={{ flex: 1 }}>{check.label}</Box>
                    {check.detail && <Box sx={{ color: "#6B7280", fontSize: 13 }}>{check.detail}</Box>}
                  </Box>
                ))}
              </Box>
            )}

            {review.draft?.staging_url && (
              <Box sx={{ mb: 1 }}>
                <Button startIcon={<VisibilityIcon />} onClick={() => setShowPreview((v) => !v)}>
                  {showPreview ? "Hide preview" : "Preview"}
                </Button>
                <Collapse in={showPreview} unmountOnExit>
                  <Box component="iframe" title="Chapter preview" src={review.draft.staging_url}
                    sandbox="allow-same-origin allow-scripts"
                    sx={{ width: "100%", height: 480, border: "1px solid #E5E7EB", borderRadius: 2, mt: 1 }} />
                </Collapse>
              </Box>
            )}
          </>
        )}

        {step === "done" && (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <CheckCircleIcon sx={{ fontSize: 56, color: "#16A34A" }} />
            <Typography sx={{ fontWeight: 700, fontSize: 18, mt: 1 }}>Published</Typography>
            <Typography sx={{ color: "#6B7280", mt: 0.5 }}>
              {audience ? `${audience} can read it now.` : "Students can read it now."}
            </Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ p: "16px 24px", borderTop: "1px solid #E5E7EB", display: "flex", justifyContent: "flex-end", gap: 1 }}>
        {step === "files" && (
          <>
            <Button onClick={close}>Cancel</Button>
            <Button variant="contained" disabled={htmlFiles.length !== 1} onClick={startUpload}>
              Upload & check
            </Button>
          </>
        )}
        {(step === "review" || step === "publishing") && (
          <>
            <Button onClick={close} disabled={busy}>Discard</Button>
            <Button variant="contained" color="success" disabled={!canPublish || busy} onClick={publish}
              startIcon={step === "publishing" ? <CircularProgress size={16} color="inherit" /> : null}>
              {isReplace ? `Publish v${chapter.version + 1}` : "Publish chapter"}
            </Button>
          </>
        )}
        {step === "done" && <Button variant="contained" onClick={onClose}>Done</Button>}
      </Box>
    </Drawer>
  );
};

const STEPS = [
  { key: "files", label: "Files" },
  { key: "review", label: "Check" },
  { key: "done", label: "Published" },
];

const StepBar = ({ step }) => {
  const position = { files: 0, uploading: 0, review: 1, publishing: 1, done: 2 }[step];
  return (
    <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
      {STEPS.map((s, i) => (
        <Box key={s.key} sx={{ flex: 1 }}>
          <Box sx={{ height: 4, borderRadius: 2, bgcolor: i <= position ? "#0172A6" : "#E5E7EB" }} />
          <Typography sx={{ fontSize: 12, mt: 0.5, color: i <= position ? "#0172A6" : "#9CA3AF", fontWeight: 600 }}>
            {i + 1}. {s.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const Stat = ({ label, value, tone }) => (
  <Box sx={{ border: "1px solid #E5E7EB", borderRadius: 2, p: 1.5, textAlign: "center" }}>
    <Typography sx={{ fontSize: 22, fontWeight: 700, color: tone === "bad" ? "#DC2626" : tone === "good" ? "#16A34A" : "#111827" }}>
      {value ?? 0}
    </Typography>
    <Typography sx={{ fontSize: 12, color: "#6B7280" }}>{label}</Typography>
  </Box>
);

export default ChapterUploadDrawer;
